import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Account, BalanceSettings, DailyBalance } from '../types/balance'; // Adjust path as necessary
import { Transaction } from '../types/Transaction';
import { produce } from 'immer'; // Optional: For easier state updates with nested structures
import { calculateDailyBalances } from '../utils/balanceCalculation';

// Define the state structure
interface BalanceState {
  accounts: Account[];
  balanceSettings: BalanceSettings | null; // Or a default settings object
  dailyBalances: DailyBalance[]; // Could be an object keyed by date for faster lookups
  // Optional: lastCalculation: BalanceCalculation | null;

  // Account Actions
  addAccount: (account: Account) => void;
  updateAccount: (accountId: string, updatedAccount: Partial<Account>) => void;
  deleteAccount: (accountId: string) => void;
  findAccountById: (accountId: string) => Account | undefined;

  // Settings Actions
  updateBalanceSettings: (settings: Partial<BalanceSettings>) => void;
  loadInitialSettings: (settings: BalanceSettings) => void; // To load from a config or DB

  // Balance Calculation Actions
  // This might be complex and could delegate to a hook or service
  calculateDailyBalances: (transactions: Transaction[], initialBalance: number, startDate: string) => void;
  addOrUpdateDailyBalance: (dailyBalance: DailyBalance) => void;
  getDailyBalance: (date: string) => DailyBalance | undefined;

  // Helper Functions / Selectors
  getTotalBalanceForAllAccounts: () => number;
  // getProjectedBalance: (daysAhead: number) => number; // Example of a more complex selector

  // Reset Action
  resetBalanceStore: () => void;
}

// Define initial state
const initialState: Omit<BalanceState,
  'addAccount' |
  'updateAccount' |
  'deleteAccount' |
  'findAccountById' |
  'updateBalanceSettings' |
  'loadInitialSettings' |
  'calculateDailyBalances' |
  'addOrUpdateDailyBalance' |
  'getDailyBalance' |
  'getTotalBalanceForAllAccounts' |
  'resetBalanceStore'
> = {
  accounts: [],
  balanceSettings: null,
  dailyBalances: [],
  // lastCalculation: null,
};

export const useBalanceStore = create<BalanceState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Account Actions
      addAccount: (account) =>
        set(
          produce((state: BalanceState) => {
            state.accounts.push(account);
          })
        ),
      updateAccount: (accountId, updatedAccount) =>
        set(
          produce((state: BalanceState) => {
            const accountIndex = state.accounts.findIndex((acc) => acc.id === accountId);
            if (accountIndex !== -1) {
              state.accounts[accountIndex] = { ...state.accounts[accountIndex], ...updatedAccount };
            }
          })
        ),
      deleteAccount: (accountId) =>
        set(
          produce((state: BalanceState) => {
            state.accounts = state.accounts.filter((acc) => acc.id !== accountId);
          })
        ),
      findAccountById: (accountId) => {
        return get().accounts.find((acc) => acc.id === accountId);
      },

      // Settings Actions
      updateBalanceSettings: (settingsUpdate) =>
        set(
          produce((state: BalanceState) => {
            const currentSettings = state.balanceSettings || {};
            state.balanceSettings = { ...currentSettings, ...settingsUpdate };
          })
        ),
      loadInitialSettings: (settings) =>
        set(
            produce((state: BalanceState) => {
                state.balanceSettings = settings;
            })
        ),

      // Balance Calculation Actions
      calculateDailyBalances: (transactions, initialBalance, startDate) => {
        // Use the utility function to calculate daily balances
        const newDailyBalances = calculateDailyBalances(transactions, initialBalance);
        set({ dailyBalances: newDailyBalances });
      },
      addOrUpdateDailyBalance: (dailyBalance) =>
        set(
          produce((state: BalanceState) => {
            const index = state.dailyBalances.findIndex(db => db.date === dailyBalance.date);
            if (index !== -1) {
              state.dailyBalances[index] = dailyBalance;
            } else {
              state.dailyBalances.push(dailyBalance);
              // Optional: sort dailyBalances by date
              state.dailyBalances.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
            }
          })
        ),
      getDailyBalance: (date: string) => {
        return get().dailyBalances.find(db => db.date === date);
      },

      // Helper Functions / Selectors
      getTotalBalanceForAllAccounts: () => {
        return get().accounts
          .filter(account => account.includedInTotal && account.isActive)
          .reduce((total, account) => total + account.currentBalance, 0);
      },

      // Reset Action
      resetBalanceStore: () => set(initialState),
    }),
    {
      name: 'balance-store', // Name for localStorage key
      storage: createJSONStorage(() => localStorage), // Or sessionStorage
      partialize: (state) => ({ 
        accounts: state.accounts, 
        balanceSettings: state.balanceSettings 
        // Exclude dailyBalances from persistence - they should be recalculated on app startup
      }),
      onRehydrateStorage: () => (state) => {
        // After hydration, recalculate dailyBalances from current transaction data
        if (state) {
          console.log('🔄 [BalanceStore] Hydrated - scheduling dailyBalances recalculation');
          // Note: We'll need to trigger recalculation once we have access to transactions
          // This will be handled by a separate initialization hook or service
        }
      },
    }
  )
);

// Optional: Export individual actions or selectors if needed directly
// export const { addAccount, getTotalBalance } = useBalanceStore.getState();
